/**
 * Helpers for the backend's `Page[T]` envelope.
 *
 * Two consumption styles share it: `usePagedQuery` follows `next_cursor`
 * (infinite "load more"), `usePageQuery` requests numbered pages with a server
 * `ordering` (sortable tables).
 */
import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

/** The backend's page envelope (cursor or page-number mode). */
export interface Page<T> {
  items: T[];
  next_cursor?: string | null;
  total?: number | null;
  page?: number | null;
  pages?: number | null;
  page_size?: number | null;
}

/** Fetch every cursor page for a bounded operational view without silent truncation. */
export async function collectPages<T>(
  fetchPage: (cursor: string | undefined) => Promise<Page<T>>,
): Promise<T[]> {
  const items: T[] = [];
  const seen = new Set<string>();
  let cursor: string | undefined;
  do {
    const page = await fetchPage(cursor);
    items.push(...page.items);
    cursor = page.next_cursor ?? undefined;
    if (cursor) {
      if (seen.has(cursor)) throw new Error("The server returned a repeated page cursor");
      seen.add(cursor);
    }
  } while (cursor);
  return items;
}

/** Table state driving a page-number query. */
export interface ListState {
  /** 1-based page. */
  page: number;
  /** Rows per page. */
  size: number;
  /** Server ordering string, e.g. `"-due_at"`. */
  sort?: string;
}

/** Query parameters sent for a page-number request. */
export interface PageParams {
  page: number;
  limit: number;
  ordering?: string;
}

/** Result of `usePageQuery` for table consumption. */
export interface PageOf<T> {
  items: T[];
  total: number;
  page: number;
  pages: number;
  pageSize: number;
  query: UseQueryResult<Page<T>>;
}

/**
 * Fetch one numbered page. Previous data is kept while the next page or sort
 * loads so the table does not flash; `key` must include every filter.
 */
export function usePageQuery<T>(
  key: readonly unknown[],
  state: ListState,
  fetchPage: (params: PageParams) => Promise<Page<T>>,
  options: { enabled?: boolean } = {},
): PageOf<T> {
  const params: PageParams = { page: state.page, limit: state.size, ordering: state.sort };
  const query = useQuery({
    queryKey: [...key, params],
    queryFn: () => fetchPage(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
  const data = query.data;
  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    page: data?.page ?? state.page,
    pages: data?.pages ?? 1,
    pageSize: data?.page_size ?? state.size,
    query,
  };
}

/** Result of `usePagedQuery` flattened for table consumption. */
export interface Paged<T> {
  items: T[];
  total: number | null;
  hasMore: boolean;
  loadMore: () => void;
  isLoadingMore: boolean;
  query: UseInfiniteQueryResult<InfiniteData<Page<T>>>;
}

/**
 * Wrap a cursor-paginated fetcher in an infinite query.
 *
 * `fetchPage` receives the cursor (undefined for the first page) and must
 * return a `Page<T>`; `key` should include every filter that affects results.
 */
export function usePagedQuery<T>(
  key: readonly unknown[],
  fetchPage: (cursor: string | undefined) => Promise<Page<T>>,
  options: { enabled?: boolean } = {},
): Paged<T> {
  const query = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: options.enabled,
  });
  const pages = query.data?.pages ?? [];
  return {
    items: pages.flatMap((p) => p.items),
    total: pages[0]?.total ?? null,
    hasMore: query.hasNextPage,
    loadMore: (): void => void query.fetchNextPage(),
    isLoadingMore: query.isFetchingNextPage,
    query,
  };
}

/** Drop undefined/empty values so they are not serialised as query params. */
export function compactParams<T extends Record<string, unknown>>(params: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") {
      out[k as keyof T] = v as T[keyof T];
    }
  }
  return out;
}
